<template>
    <v-card tile color="#f7f7f7" light :flat="minimal" elevation="0">
        <v-container fluid class="pa-0">

            <v-row no-gutters :class="minimal ? 'body-wrapper-minimal' : 'body-wrapper'">

                <!-- HEADER -->
                <v-col cols="12" align="center" v-if="subPanel">
                    <email-header :logo-url="subPanel.logo_url"
                                  :visible="settings.showHeader"
                                  :edit-mode="editMode"
                    ></email-header>
                </v-col>

                <!-- EMAIL BODY -->
                <v-col cols="12" align="center">
                    <email-body v-model="emailTranslationsCopy.body"
                                background-color="white"
                                class="grey--text text--darken-1"
                                :edit-mode="editMode"
                                @input="emailTranslationsChanged"
                    ></email-body>
                </v-col>

                <!-- ACTION BUTTON -->
                <v-col cols="12" align="center">
                    <email-action-button v-model="emailTranslationsCopy.action_button_text"
                                         :color="settings.actionButtonColor"
                                         :edit-mode="editMode"
                                         :url="project.start_url"
                                         @input="emailTranslationsChanged"
                    ></email-action-button>
                </v-col>

                <!-- EMAIL SIGNATURE -->
                <v-col cols="12" align="center">
                    <email-signature v-model="emailTranslationsCopy.signature_block"
                                     background-color="white"
                                     class="grey--text text--darken-1"
                                     @input="emailTranslationsChanged"
                                     :visible="settings.showSignature"
                                     :edit-mode="editMode"
                    ></email-signature>
                </v-col>

                <!-- PROJECT INFORMATION -->
                <v-col cols="12" class="grey--text text--darken-1" align="center"
                       v-if="projectInfoVisible">
                    <email-project-info v-model="subPanelTranslationsCopy"
                                        :type="type"
                                        :visible="settings.showProjectInfo"
                                        :project="project"
                                        :editable="!editEmailOnly && editMode"
                                        :color="settings.actionButtonColor"
                                        @input="subPanelTranslationsChanged"
                    ></email-project-info>
                </v-col>

                <!-- EMAIL NOTICE -->
                <v-col cols="12" align="center" v-show="!minimal">

                    <email-notice v-if="subPanel"
                                  v-model="subPanelTranslationsCopy.email_notice"
                                  :sub-panel-id="subPanel.id"
                                  :locale="locale"
                                  :editable="!editEmailOnly && editMode"
                                  @input="subPanelTranslationsChanged">
                    </email-notice>

                </v-col>
            </v-row>

            <!-- FOOTER -->
            <v-row no-gutters>
                <v-col cols="12" align="center" v-show="!minimal">
                    <email-footer v-model="subPanelTranslationsCopy.email_footer_text"
                                  v-if="subPanel"
                                  :sub-panel-id="subPanel.id"
                                  :locale="locale"
                                  :editable="!editEmailOnly && editMode"
                                  @input="subPanelTranslationsChanged"
                                  :visible="settings.showFooter">
                    </email-footer>
                </v-col>
            </v-row>

            <!-- DEBUG -->
            <v-row v-if="debug">
                <v-col cols="12">
                    <v-card tile flat>
                        <json :data="emailTranslations"></json>
                        <json :data="subPanelTranslationsCopy"></json>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <json v-if="false" :data="project"/>
    </v-card>

</template>

<script>

import Json from 'vue-json-pretty';
import EmailBody from "./EmailBody";
import EmailActionButton from "./EmailActionButton";
import EmailNotice from "./EmailNotice";
import EmailFooter from "./EmailFooter";
import EmailHeader from "./EmailHeader";
import EmailProjectInfo from "./EmailProjectInfo";
import EmailSignature from "./EmailSignature";

export default {

    components: {
        Json,
        EmailHeader,
        EmailBody,
        EmailActionButton,
        EmailProjectInfo,
        EmailNotice,
        EmailFooter,
        EmailSignature
    },

    props: {
        type: {
            type: String,
            required: true,
        },
        subPanel: {
            type: Object | null,
            required: true,
        },
        subPanelTranslations: {
            type: Object | null,
            required: true,
        },
        emailTranslations: {
            type: Object | null,
            required: true,
        },
        settings: {
            type: Object,
            required: true,
        },
        locale: {
            type: String,
            required: true,
        },
        editEmailOnly: {
            type: Boolean,
            required: false,
            default: false,
        },
        project: {
            type: Object,
            required: true,
            default: () => null,
        },
        minimal: {
            type: Boolean,
            required: false,
            default: false,
        },
        editMode: {
            type: Boolean,
            required: false,
            default: true,
        }
    },

    data() {
        return {
            debug: false,

            emailTranslationsCopy: {
                body: null,
                action_button_text: null,
                signature_block: null,
            },

            subPanelTranslationsCopy: {
                project_info_text: null,
                project_info_reward_text: null,
                project_info_reward: null,
                project_info_duration_text: null,
                project_info_duration: null,
                project_info_tablet_text: null,
                project_info_mobile_text: null,
                project_info_desktop_text: null,
                project_info_compatible_text: null,
                project_info_incompatible_text: null,
                email_notice: null,
                email_footer_text: null,
            },

            minPermission: 'view projects',
        }
    },

    computed: {
        projectInfoVisible() {
            return ['project-invitation', 'project-reminder'].includes(this.type);
        }
    },

    methods: {
        emailTranslationsChanged() {
            this.$emit('email:translations', this.emailTranslationsCopy);
        },

        subPanelTranslationsChanged() {
            this.$emit('subpanel:translations', this.subPanelTranslationsCopy);
        },
    },

    watch: {
        subPanelTranslations(translations) {
            this.subPanelTranslationsCopy = Object.assign({}, translations);
        },
        emailTranslations(translations) {
            this.emailTranslationsCopy = Object.assign({}, translations);
        }
    },

    mounted() {
        this.subPanelTranslationsCopy = Object.assign({}, this.subPanelTranslations);

        // Do not emit subject or sender name as it will override email editor subject / sender name
        this.emailTranslationsCopy = Object.assign({}, {
            body: this.emailTranslations.body,
            action_button_text: this.emailTranslations.action_button_text,
            signature_block: this.emailTranslations.signature_block
        });
    }
}
</script>

<style scoped>

.body-wrapper {
    padding: 20px;
    background-color: white;
    border-color: #e8e5ef;
    border-radius: 2px;
    border-width: 1px;
    box-shadow: 0 2px 0 rgba(0, 0, 150, 0.025), 2px 4px 0 rgba(0, 0, 150, 0.015);
    width: 570px;
    margin: auto;
}

.body-wrapper-minimal {
    padding: 20px;
    background-color: white;
    width: 570px;
    margin: auto;
}

</style>
